// Pure helper: extract the visible social links for the coming-soon splash.
// The splash reads socials from `venue.appConfig.footer.socials` (the same
// place SiteFooter reads from), so anything Tyler @ Toka saves in the
// footer editor is immediately available on the landing page. Filters out
// empty / whitespace-only values so a half-saved config can't render an
// empty pill.
//
// Protocol guard (2026-09-08 retro peer review): venue admins provide these
// URLs, so a `javascript:` payload here would fire on any visitor clicking
// the pill (React doesn't sanitise href). Same pattern email-blocks.ts
// applies, keep the allowlist tight to real link protocols.

import type { FooterSocials } from "./site-footer";

export type SocialLink = { key: keyof FooterSocials; href: string };

const SAFE_HREF = /^(https?:|mailto:|tel:)/i;

/**
 * True when the href starts with a safe link protocol. Rejects `javascript:`,
 * `data:`, and any other schemes that could execute or exfiltrate on click.
 * A missing/relative URL is rejected too, we only render links the admin
 * intentionally typed.
 */
export function isSafeSocialHref(href: string): boolean {
  return SAFE_HREF.test(href.trim());
}

export function collectComingSoonSocials(input: {
  socials: FooterSocials | null | undefined;
  hideSocials?: boolean | null | undefined;
}): SocialLink[] {
  if (input.hideSocials) return [];
  const socials = input.socials ?? {};
  const out: SocialLink[] = [];
  for (const [k, v] of Object.entries(socials)) {
    if (typeof v !== "string") continue;
    const trimmed = v.trim();
    if (!trimmed || !isSafeSocialHref(trimmed)) continue;
    out.push({ key: k as keyof FooterSocials, href: trimmed });
  }
  return out;
}
